import { MMKV } from 'react-native-mmkv';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const MIGRATION_FLAG_KEY = 'mmkv_migration_complete_v1';
const LEGACY_CACHE_KEY = '@viewing_progress_cache';

const isWeb = Platform.OS === 'web';

let mmkvInstance: MMKV | null = null;

const getMMKV = (): MMKV => {
  if (!mmkvInstance) {
    mmkvInstance = new MMKV({
      id: 'viewing-progress-storage',
    });
  }
  return mmkvInstance;
};

interface ProgressStorage {
  getString(key: string): string | undefined;
  setString(key: string, value: string): void;
  delete(key: string): void;
  contains(key: string): boolean;
}

const createWebStorage = (): ProgressStorage => ({
  getString: (key: string): string | undefined => {
    try {
      const value = localStorage.getItem(key);
      return value ?? undefined;
    } catch {
      return undefined;
    }
  },
  setString: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.error('[STORAGE] Web localStorage write error:', e);
    }
  },
  delete: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {}
  },
  contains: (key: string): boolean => {
    try {
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  },
});

const createNativeStorage = (): ProgressStorage => {
  const mmkv = getMMKV();
  return {
    getString: (key: string): string | undefined => mmkv.getString(key),
    setString: (key: string, value: string): void => mmkv.set(key, value),
    delete: (key: string): void => mmkv.delete(key),
    contains: (key: string): boolean => mmkv.contains(key),
  };
};

export const progressStorage: ProgressStorage = isWeb
  ? createWebStorage()
  : createNativeStorage();

export const migrateFromAsyncStorage = async (): Promise<boolean> => {
  if (isWeb) {
    return true;
  }

  try {
    if (progressStorage.contains(MIGRATION_FLAG_KEY)) {
      console.log('[STORAGE] Migration already completed, skipping');
      return true;
    }

    console.log('[STORAGE] Starting migration from AsyncStorage to MMKV...');
    const startTime = Date.now();

    const legacyData = await AsyncStorage.getItem(LEGACY_CACHE_KEY);

    if (!legacyData) {
      console.log('[STORAGE] No legacy data found, marking migration complete');
      progressStorage.setString(MIGRATION_FLAG_KEY, 'true');
      return true;
    }

    JSON.parse(legacyData);

    progressStorage.setString(LEGACY_CACHE_KEY, legacyData);

    progressStorage.setString(MIGRATION_FLAG_KEY, 'true');

    await AsyncStorage.removeItem(LEGACY_CACHE_KEY);

    const duration = Date.now() - startTime;
    console.log(`[STORAGE] Migration completed successfully in ${duration}ms`);

    return true;
  } catch (error) {
    console.error('[STORAGE] Migration failed:', error);
    return false;
  }
};

export const clearAllProgressData = async (): Promise<void> => {
  progressStorage.delete(LEGACY_CACHE_KEY);
  progressStorage.delete(MIGRATION_FLAG_KEY);

  if (!isWeb) {
    await AsyncStorage.removeItem(LEGACY_CACHE_KEY).catch(() => {});
  }
};
